import { holdoutRulesDefaults } from '../s2/defaults';
import { ENTRANCE_ENTRY, MAX_WAVES } from '../s2/holdoutPreview';
import { NAV_AREAS, PLACEABLE_KINDS, type HoldoutDef, type PlaceableDef } from '../s2/types';
import type { Err, Ids } from './common';
import { idsIn, rangeMsg } from './s2common';
import type { Bundle } from './types';

type At = (path: string, message: string) => void;

/**
 * Port of shared-cs `ContentValidator.Holdouts.cs` (`HoldoutSections`), S2-WAV: zombies' barricadeDps, weapon bloom, items' placeable,
 * holdouts (entries, waves, crates, limits, squad scale), holdoutRules, and the world events' holdouts. Same errors, same wording, same
 * order.
 */
export function validateHoldouts(b: Bundle, err: Err, warn: Err, ids: Ids): void {
  void warn;
  void ids;
  const range = (
    at: At,
    path: string,
    v: number,
    min: number,
    max: number,
    where: string,
    field: string,
  ) => {
    const m = rangeMsg(v, min, max, where, field);
    if (m) at(path, m);
  };

  const zombies = new Set<string>();
  (b.zombies ?? []).forEach((z, i) => {
    if (z == null) return;
    zombies.add(z.id ?? '');
    const dps = (z as { barricadeDps?: number }).barricadeDps ?? 0;
    range(
      (p, m) => err('zombies', i, p, m),
      'barricadeDps',
      dps,
      0,
      1000,
      `Zombie '${z.id ?? ''}'`,
      'barricadeDps',
    );
  });

  const placeables = new Set<string>();
  (b.items ?? []).forEach((item, i) => {
    if (item == null) return;
    const at: At = (p, m) => err('items', i, p, m);
    if (item.weapon != null)
      bloom(item.id ?? '', item.weapon as Record<string, number | undefined>, at, range);
    if (item.placeable == null) return;
    placeables.add(item.id ?? '');
    placeable(item, item.placeable, at, range);
  });

  const tables = idsIn(b.lootTables);
  const categories = idsIn(b.categories);
  const setPieces = idsIn(b.setPieces);

  const holdoutIds = new Set<string>();
  (b.holdouts ?? []).forEach((h, i) => {
    const at: At = (p, m) => err('holdouts', i, p, m);
    if (h == null) {
      at('', 'A holdout is empty.');
      return;
    }
    holdout(h, at, range, holdoutIds, categories, setPieces, zombies, tables, placeables);
  });

  const r = b.holdoutRules ?? holdoutRulesDefaults();
  const rat: At = (p, m) => err('holdoutRules', undefined, p, m);
  const cap = { ...holdoutRulesDefaults().liveCap, ...(r.liveCap ?? {}) } as {
    low: number;
    mid: number;
    high: number;
  };
  if (!(cap.low >= 4 && cap.low <= cap.mid && cap.mid <= cap.high && cap.high <= 60))
    rat('liveCap', 'holdoutRules.liveCap needs 4 ≤ low ≤ mid ≤ high ≤ 60.');
  range(rat, 'corpseCap', r.corpseCap ?? 12, 0, 40, 'holdoutRules', 'corpseCap');
  for (const [k, v] of Object.entries(r.areaCost ?? {})) {
    if (!(NAV_AREAS as readonly string[]).includes(k))
      rat(
        `areaCost.${k}`,
        `holdoutRules.areaCost names unknown area '${k}' (use barricade, wire, spikes).`,
      );
    range(rat, `areaCost.${k}`, v, 0, 100, 'holdoutRules', `areaCost.${k}`);
  }
  range(rat, 'spillQueue', r.spillQueue ?? 8, 1, 50, 'holdoutRules', 'spillQueue');
  const st = { count: 3, afterS: 20, ...(r.stragglers ?? {}) };
  range(rat, 'stragglers.count', st.count, 0, 20, 'holdoutRules', 'stragglers.count');
  range(rat, 'stragglers.afterS', st.afterS, 0, 120, 'holdoutRules', 'stragglers.afterS');
  range(rat, 'doorBashHp', r.doorBashHp ?? 15, 1, 1000, 'holdoutRules', 'doorBashHp');

  (b.worldEvents?.types ?? []).forEach((t, j) => {
    if (t == null) return;
    const hold = (t as { holdoutIds?: string[] | null }).holdoutIds;
    if (t.id === 'siege' && (hold == null || hold.length === 0))
      err(
        'worldEvents',
        undefined,
        `types.${j}.holdoutIds`,
        "World event 'siege' needs at least one holdout.",
      );
    for (const id of hold ?? [])
      if (!holdoutIds.has(id))
        err(
          'worldEvents',
          undefined,
          `types.${j}.holdoutIds`,
          `World event '${t.id ?? ''}' names unknown holdout '${id}'.`,
        );
  });
}

type Range = (
  at: At,
  path: string,
  v: number,
  min: number,
  max: number,
  where: string,
  field: string,
) => void;

function holdout(
  h: HoldoutDef,
  at: At,
  range: Range,
  ids: Set<string>,
  categories: Set<string>,
  setPieces: Set<string>,
  zombies: Set<string>,
  tables: Set<string>,
  placeables: Set<string>,
) {
  const where = `Holdout '${h.id ?? ''}'`;
  if (!h.id) at('id', 'A holdout has no id.');
  else if (ids.has(h.id)) at('id', `${where} is listed twice.`);
  else ids.add(h.id);
  if (!h.name) at('name', `${where} needs a name.`);
  if (!h.categories || h.categories.length === 0)
    at('categories', `${where} needs at least one building category.`);
  for (const c of h.categories ?? [])
    if (!categories.has(c)) at('categories', `${where} names unknown category '${c}'.`);
  if (h.setPiece && !setPieces.has(h.setPiece))
    at('setPiece', `${where} names unknown set piece '${h.setPiece}'.`);
  range(at, 'minFootprintM2', h.minFootprintM2 ?? 0, 0, 100000, where, 'minFootprintM2');
  range(at, 'setupS', h.setupS ?? 90, 0, 600, where, 'setupS');
  range(at, 'breatherS', h.breatherS ?? 20, 0, 120, where, 'breatherS');

  const waves = h.waves ?? [];
  const lastFrom = Math.max(1, waves.length);
  const entries = new Set<string>();
  (h.entries ?? []).forEach((e, k) => {
    if (e == null || !e.id) {
      at(`entries.${k}.id`, `${where} has an entry with no id.`);
      return;
    }
    if (entries.has(e.id)) at(`entries.${k}.id`, `${where} lists entry '${e.id}' twice.`);
    entries.add(e.id);
    const from = e.from ?? 1;
    if (from < 1 || from > lastFrom)
      at(`entries.${k}.from`, `${where} entry '${e.id}' needs from in 1..${lastFrom}.`);
  });

  if (waves.length < 1 || waves.length > MAX_WAVES)
    at('waves', `${where} needs 1..${MAX_WAVES} waves.`);
  waves.forEach((w, n) => {
    const ww = `${where} wave ${n + 1}`;
    const p = (f: string) => `waves.${n}${f ? `.${f}` : ''}`;
    if (w == null) {
      at(p(''), `${ww} is empty.`);
      return;
    }
    let bodies = 0;
    let bossInMix = false;
    (w.mix ?? []).forEach((m, k) => {
      if (m == null || !zombies.has(m.zombieId ?? ''))
        at(p(`mix.${k}`), `${ww} names unknown zombie '${m?.zombieId ?? ''}'.`);
      else if ((m.count ?? 0) < 1 || (m.count ?? 0) > 200)
        at(p(`mix.${k}`), `${ww} needs a count in 1..200 for '${m.zombieId}'.`);
      else bodies += m.count ?? 0;
      if (m != null && (m.zombieId ?? '') === (w.boss ?? '')) bossInMix = true;
    });
    if (bodies === 0) at(p('mix'), `${ww} has no zombies.`);
    if (!w.entries || w.entries.length === 0) at(p('entries'), `${ww} needs at least one entry.`);
    let anyOpen = false;
    for (const e of w.entries ?? []) {
      if (e !== ENTRANCE_ENTRY && !entries.has(e))
        at(p('entries'), `${ww} names unknown entry '${e}'.`);
      const def = (h.entries ?? []).find((x) => x != null && x.id === e);
      if (e === ENTRANCE_ENTRY || def == null || (def.from ?? 1) <= n + 1) anyOpen = true;
    }
    if (w.entries && w.entries.length > 0 && !anyOpen)
      at(p('entries'), `${ww} has no entry open yet (check each entry's from).`);
    range(at, p('spawnIntervalS'), w.spawnIntervalS ?? 1, 0.2, 5, ww, 'spawnIntervalS');
    if (w.boss && !bossInMix) at(p('boss'), `${ww}'s boss '${w.boss}' must be in its mix.`);
    if (w.crate != null) {
      if (!tables.has(w.crate.lootTableId ?? ''))
        at(p('crate'), `${ww}'s crate names unknown loot table '${w.crate.lootTableId ?? ''}'.`);
      const rolls = w.crate.rolls ?? 0;
      if (rolls < 1 || rolls > 20) at(p('crate'), `${ww}'s crate needs rolls in 1..20.`);
    }
  });

  for (const [k, v] of Object.entries(h.placementLimits ?? {})) {
    if (!placeables.has(k))
      at(`placementLimits.${k}`, `${where} limits '${k}', which isn't a placeable item.`);
    if (v < 0 || v > 50)
      at(`placementLimits.${k}`, `${where} needs a placement limit in 0..50 for '${k}'.`);
  }
  if (h.squadScale != null && h.squadScale.length > 4)
    at('squadScale', `${where} lists squadScale for at most 4 squad sizes.`);
  (h.squadScale ?? []).forEach((s, k) =>
    range(at, `squadScale.${k}`, s, 0.1, 3, where, 'squadScale'),
  );
}

function bloom(id: string, w: Record<string, number | undefined>, at: At, range: Range) {
  const where = `Weapon '${id}'`;
  const per = w.bloomPerShotDeg ?? 0;
  const max = w.maxBloomDeg ?? 0;
  range(at, 'weapon.bloomPerShotDeg', per, 0, 10, where, 'bloomPerShotDeg');
  range(at, 'weapon.maxBloomDeg', max, 0, 45, where, 'maxBloomDeg');
  range(
    at,
    'weapon.bloomRecoverDegPerS',
    w.bloomRecoverDegPerS ?? 20,
    0,
    200,
    where,
    'bloomRecoverDegPerS',
  );
  range(at, 'weapon.fireMoveMult', w.fireMoveMult ?? 0, 0, 1, where, 'fireMoveMult');
  if (per > 0 && !(max > 0))
    at('weapon.maxBloomDeg', `${where} blooms, so it needs maxBloomDeg above 0.`);
}

function placeable(
  item: NonNullable<Bundle['items']>[number],
  p: PlaceableDef,
  at: At,
  range: Range,
) {
  const where = `Placeable '${item.id ?? ''}'`;
  if (item.weapon != null || item.armor != null || item.throwable != null)
    at(
      'placeable',
      `Item '${item.id ?? ''}' can't be a placeable and a weapon, armor or throwable.`,
    );
  const kind = p.kind ?? '';
  if (!(PLACEABLE_KINDS as readonly string[]).includes(kind)) {
    at(
      'placeable.kind',
      `${where} has unknown kind '${kind}' (use ${PLACEABLE_KINDS.join(', ')}).`,
    );
    return;
  }
  const fp = p.footprint ?? [];
  if (fp.length !== 0 && (fp.length !== 2 || fp[0]! < 1 || fp[1]! < 1 || fp[0]! > 6 || fp[1]! > 6))
    at('placeable.footprint', `${where} needs a footprint of [w, h] cells in 1..6.`);
  const f = (k: string, v: number | undefined, d: number, min: number, max: number) =>
    range(at, `placeable.${k}`, v ?? d, min, max, where, k);
  f('placeS', p.placeS, 1, 0, 30);
  f('noiseRadiusM', p.noiseRadiusM, 0, 0, 150);
  f('friendlyFireMult', p.friendlyFireMult, 0, 0, 1);
  if (p.navArea && !(NAV_AREAS as readonly string[]).includes(p.navArea))
    at('placeable.navArea', `${where} has unknown navArea '${p.navArea}'.`);
  switch (kind) {
    case 'mine': {
      f('arcDeg', p.arcDeg, 0, 1, 180);
      f('rangeM', p.rangeM, 0, 0.5, 20);
      const rangeM = p.rangeM ?? 0;
      f('tripM', p.tripM, 0, 0.1, rangeM > 0 ? rangeM : 20);
      f('damage', p.damage, 0, 1, 2000);
      f('falloffMin', p.falloffMin, 0, 0, 1);
      f('knockbackM', p.knockbackM, 0, 0, 5);
      f('backBlastM', p.backBlastM, 0, 0, 5);
      f('armS', p.armS, 0, 0, 10);
      break;
    }
    case 'barricade':
      f('hp', p.hp, 0, 1, 5000);
      if (p.navArea !== 'barricade')
        at('placeable.navArea', `${where} is a barricade and needs navArea 'barricade'.`);
      break;
    case 'trap':
      f('hp', p.hp, 0, 1, 5000);
      f('slowMult', p.slowMult, 1, 0.05, 1);
      f('dps', p.dps, 0, 0, 200);
      f('wear', p.wear, 0, 0, 1000);
      if (p.navArea !== 'wire' && p.navArea !== 'spikes')
        at('placeable.navArea', `${where} is a trap and needs navArea 'wire' or 'spikes'.`);
      break;
    case 'light':
      f('lightRadiusM', p.lightRadiusM, 0, 0.5, 30);
      f('arcDeg', p.arcDeg, 0, 0, 360);
      break;
  }
}
